import React, { useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Brain, Compass, CalendarDays, PenLine, LineChart, GitBranch,
  ArrowRight, Zap, Check, Film, Target, Sparkles,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/lib/auth";

const HERO_IMG = "https://images.unsplash.com/photo-1512813498716-3e640fed3f39?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODh8MHwxfHNlYXJjaHw0fHxjb250ZW50JTIwY3JlYXRvciUyMGZpbG1pbmclMjBwb3J0cmFpdHxlbnwwfHx8fDE3ODI1MzY1MzV8MA&ixlib=rb-4.1.0&q=85";

const EMPLOYEES = [
  { icon: Brain, tag: "01", name: "AI Brand Strategist", line: "Learns your business, offer, customer & voice in a single onboarding.", span: "col-span-12 md:col-span-7 row-span-2" },
  { icon: Compass, tag: "02", name: "AI Funnel Strategist", line: "Buckets every idea TOF, MOF or BOF — with the right metric for the job.", span: "col-span-12 md:col-span-5" },
  { icon: CalendarDays, tag: "03", name: "AI Content Strategist", line: "Writes your Weekly Revenue Content Plan every Monday.", span: "col-span-12 md:col-span-5" },
  { icon: PenLine, tag: "04", name: "AI Creative Director", line: "Turns every idea into hook variants — Control, Contrarian, Curiosity, Pain.", span: "col-span-12 md:col-span-6" },
  { icon: LineChart, tag: "05", name: "AI Performance Analyst", line: "Finds patterns in what worked. Every week, sharper than the last.", span: "col-span-12 md:col-span-6" },
  { icon: GitBranch, tag: "06", name: "AI Nurture Architect", line: "Builds belief journeys that move strangers to buyers.", span: "col-span-12 md:col-span-7" },
];

const TIERS = [
  { name: "Creator", price: 79, features: ["1 brand", "3 platforms", "Weekly strategy", "30 content ideas / mo", "Full scripts", "Performance analysis"], testid: "tier-creator" },
  { name: "Pro", price: 149, popular: true, features: ["Everything in Creator", "Unlimited platforms", "Daily strategy", "Unlimited scripts", "A/B hook testing", "Belief journeys", "Advanced analytics"], testid: "tier-pro" },
  { name: "Team", price: 399, features: ["Everything in Pro", "Multiple users", "Multiple offers", "Approval workflow", "Team calendar", "Brand controls", "Reporting"], testid: "tier-team" },
];

const LOOP = [
  { k: "UNDERSTAND", d: "Your business, offer, customer, voice." },
  { k: "STRATEGIZE", d: "This week's plan — every post has a job." },
  { k: "CREATE",     d: "Hook, script, b-roll, caption, CTA." },
  { k: "PUBLISH",    d: "Where, when, in what format." },
  { k: "MEASURE",    d: "Manual import. Pattern detection." },
  { k: "LEARN",      d: "What worked. What failed. Why." },
  { k: "ADAPT",      d: "Next week's plan updates itself." },
];

export default function Landing() {
  const { user } = useAuth();
  const nav = useNavigate();

  useEffect(() => { document.documentElement.classList.add("dark"); }, []);

  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* NAV */}
      <header className="sticky top-0 z-40 border-b border-border backdrop-blur-xl bg-background/70">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="h-8 w-8 grid place-items-center bg-primary text-primary-foreground rounded">
              <Zap className="h-4 w-4" strokeWidth={2.5}/>
            </div>
            <div className="font-display font-black tracking-tight text-xl">ContentOS</div>
          </div>
          <nav className="hidden md:flex items-center gap-8 text-sm font-medium">
            <a href="#employees" className="text-muted-foreground hover:text-foreground transition-colors">The 6 AI Employees</a>
            <a href="#loop" className="text-muted-foreground hover:text-foreground transition-colors">The Loop</a>
            <a href="#pricing" className="text-muted-foreground hover:text-foreground transition-colors">Pricing</a>
          </nav>
          <div className="flex items-center gap-3">
            {user ? (
              <Button data-testid="nav-goto-app" onClick={() => nav("/app")}>Open app <ArrowRight className="h-4 w-4"/></Button>
            ) : (
              <>
                <Link to="/auth" data-testid="nav-signin" className="text-sm font-medium text-muted-foreground hover:text-foreground">Sign in</Link>
                <Button data-testid="nav-cta" onClick={() => nav("/pricing")}>Pick a plan <ArrowRight className="h-4 w-4"/></Button>
              </>
            )}
          </div>
        </div>
      </header>

      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 grid-lines opacity-50 pointer-events-none" />
        <div className="max-w-7xl mx-auto px-6 pt-24 pb-32 grid grid-cols-12 gap-8 relative">
          <div className="col-span-12 lg:col-span-7 reveal">
            <div className="inline-flex items-center gap-2 border border-border rounded-full px-3 py-1 text-xs uppercase tracking-widest font-semibold text-muted-foreground">
              <span className="h-1.5 w-1.5 rounded-full bg-primary" />
              Your AI Organic Marketing Department
            </div>
            <h1 className="mt-6 font-display font-black text-5xl sm:text-6xl lg:text-7xl leading-[0.9] tracking-tighter">
              Never wonder<br/>what to post<br/>
              <span className="text-primary">again.</span>
            </h1>
            <p className="mt-8 text-lg text-muted-foreground max-w-xl leading-relaxed">
              ContentOS learns your business, offer, customers and voice. Then it builds your organic strategy, tells you exactly what to post, writes every hook, script and CTA — and shows you where and when to publish it.
            </p>
            <p className="mt-4 text-lg font-semibold">You film. We handle the thinking.</p>
            <div className="mt-10 flex flex-wrap gap-3">
              <Button data-testid="hero-cta-primary" size="lg" onClick={() => nav("/pricing")} className="h-12 px-6 text-base">
                Pick your plan <ArrowRight className="h-4 w-4" />
              </Button>
              <Button data-testid="hero-cta-secondary" size="lg" onClick={() => nav("/pricing")} className="h-12 px-6 text-base">
                See pricing <ArrowRight className="h-4 w-4" />
              </Button>
            </div>
            <div className="mt-10 grid grid-cols-3 gap-6 max-w-lg">
              {[["30–60 min", "Onboarding"], ["7 posts", "Weekly plan"], ["4 variants", "Per hook"]].map(([n, l]) => (
                <div key={l}>
                  <div className="font-display font-black text-3xl">{n}</div>
                  <div className="text-xs uppercase tracking-widest text-muted-foreground mt-1">{l}</div>
                </div>
              ))}
            </div>
          </div>
          <div className="col-span-12 lg:col-span-5 relative reveal reveal-delay-2">
            <div className="relative aspect-[4/5] rounded-md overflow-hidden border border-border">
              <img src={HERO_IMG} alt="Creator filming" className="absolute inset-0 h-full w-full object-cover" />
              <div className="absolute inset-0 bg-black/40" />
              <div className="absolute bottom-6 left-6 right-6">
                <div className="text-xs uppercase tracking-widest text-white/70">Today · Tuesday · 8:30 AM</div>
                <div className="mt-2 font-display text-white text-2xl font-bold leading-tight">
                  “You don't have a content consistency problem. You have a content <span className="text-primary">purpose</span> problem.”
                </div>
                <div className="mt-3 flex items-center gap-2 text-xs text-white/80"><Film className="h-3.5 w-3.5"/> Film this — Reel · IG · Hook: contrarian</div>
              </div>
            </div>
            <div className="absolute -bottom-6 -left-6 bg-card border border-border rounded p-4 max-w-xs hidden md:block">
              <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-muted-foreground"><Sparkles className="h-3.5 w-3.5 text-primary"/> Weekly review</div>
              <div className="mt-2 text-sm">
                <span className="font-semibold">Expensive-mistake hooks</span> generated <span className="font-bold text-primary">2.4×</span> more shares than your average. Next week: 3 more.
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* EMPLOYEES */}
      <section id="employees" className="border-t border-border bg-card">
        <div className="max-w-7xl mx-auto px-6 py-24">
          <div className="grid grid-cols-12 gap-8 mb-14">
            <div className="col-span-12 md:col-span-5">
              <div className="text-xs uppercase tracking-widest text-muted-foreground font-bold">The offer stack</div>
              <h2 className="mt-3 font-display text-4xl sm:text-5xl font-black tracking-tighter leading-none">Six AI employees.<br/>One monthly bill.</h2>
            </div>
            <div className="col-span-12 md:col-span-6 md:col-start-7 text-lg text-muted-foreground leading-relaxed">
              This is how you compete against an <span className="text-foreground font-semibold">ENTIRE</span> organic marketing department that companies are paying thousands for: Content strategist ($3k/mo), Copywriter ($2k), Social Manager ($2.5k), Creative Strategist ($4k) — ContentOS starts at just <span className="text-foreground font-semibold">$79/month</span>.
            </div>
          </div>

          <div className="grid grid-cols-12 gap-4">
            {EMPLOYEES.map((e, i) => (
              <div
                key={e.name}
                className={`${e.span} border border-border bg-background p-6 md:p-8 rounded transition-transform duration-200 hover:-translate-y-1`}
                data-testid={`employee-${i + 1}`}
              >
                <div className="flex items-start justify-between">
                  <e.icon className="h-6 w-6 text-primary" strokeWidth={1.75}/>
                  <div className="font-mono text-xs text-muted-foreground">{e.tag}</div>
                </div>
                <div className="mt-6 font-display font-bold text-2xl leading-tight tracking-tight">{e.name}</div>
                <div className="mt-3 text-muted-foreground leading-relaxed">{e.line}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* LOOP */}
      <section id="loop" className="border-t border-border">
        <div className="max-w-7xl mx-auto px-6 py-24">
          <div className="text-xs uppercase tracking-widest text-muted-foreground font-bold">The Loop</div>
          <h2 className="mt-3 font-display text-4xl sm:text-5xl font-black tracking-tighter leading-none max-w-3xl">
            Every other tool helps you make more content.<br/>
            <span className="text-primary">We help you make more informed content.</span>
          </h2>

          <div className="mt-14 grid grid-cols-1 md:grid-cols-7 border border-border rounded overflow-hidden">
            {LOOP.map((s, i) => (
              <div key={s.k} className={`p-6 border-b md:border-b-0 md:border-r border-border last:border-r-0 ${i === 6 ? "bg-primary text-primary-foreground" : ""}`}>
                <div className={`font-mono text-xs ${i === 6 ? "text-primary-foreground/70" : "text-muted-foreground"}`}>0{i + 1}</div>
                <div className={`mt-4 font-display font-black text-xl tracking-tight`}>{s.k}</div>
                <div className={`mt-2 text-sm leading-relaxed ${i === 6 ? "text-primary-foreground/90" : "text-muted-foreground"}`}>{s.d}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PRICING */}
      <section id="pricing" className="border-t border-border bg-card">
        <div className="max-w-7xl mx-auto px-6 py-24">
          <div className="grid grid-cols-12 gap-8 mb-14">
            <div className="col-span-12 md:col-span-5">
              <div className="text-xs uppercase tracking-widest text-muted-foreground font-bold">Pricing</div>
              <h2 className="mt-3 font-display text-4xl sm:text-5xl font-black tracking-tighter leading-none">
                Marketing Team: <span className="line-through text-muted-foreground">$11,500/mo</span>.<br/>
                ContentOS: <span className="text-primary">$79/mo.</span>
              </h2>
            </div>
            <div className="col-span-12 md:col-span-6 md:col-start-7 text-lg text-muted-foreground leading-relaxed">
              <div className="flex items-center gap-2 py-1"><Target className="h-4 w-4 text-primary"/> Content strategist — $3,000/mo</div>
              <div className="flex items-center gap-2 py-1"><Target className="h-4 w-4 text-primary"/> Copywriter — $2,000/mo</div>
              <div className="flex items-center gap-2 py-1"><Target className="h-4 w-4 text-primary"/> Social media manager — $2,500/mo</div>
              <div className="flex items-center gap-2 py-1"><Target className="h-4 w-4 text-primary"/> Creative strategist — $4,000/mo</div>
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-4">
            {TIERS.map((t) => (
              <div
                key={t.name}
                data-testid={t.testid}
                className={`p-8 rounded border ${t.popular ? "border-primary bg-background" : "border-border bg-background"} relative`}
              >
                {t.popular && (
                  <div className="absolute -top-3 left-6 bg-primary text-primary-foreground text-xs font-bold uppercase tracking-widest px-3 py-1 rounded">Most popular</div>
                )}
                <div className="font-display font-bold text-2xl tracking-tight">{t.name}</div>
                <div className="mt-4 flex items-baseline gap-1">
                  <span className="font-display font-black text-5xl">${t.price}</span>
                  <span className="text-muted-foreground">/mo</span>
                </div>
                <ul className="mt-6 space-y-3">
                  {t.features.map((f) => (
                    <li key={f} className="flex items-start gap-2 text-sm">
                      <Check className="h-4 w-4 text-primary mt-0.5 shrink-0"/>
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
                <Button data-testid={`${t.testid}-cta`} onClick={() => nav("/pricing")} className="mt-8 w-full h-11">
                  Choose {t.name}
                </Button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* GRAND SLAM */}
      <section className="border-t border-border">
        <div className="max-w-5xl mx-auto px-6 py-32 text-center">
          <div className="text-xs uppercase tracking-widest text-muted-foreground font-bold">The Grand Slam Offer</div>
          <h2 className="mt-4 font-display font-black text-5xl sm:text-6xl lg:text-7xl tracking-tighter leading-[0.95]">
            You film.<br/>We handle the <span className="text-primary">thinking.</span>
          </h2>
          <p className="mt-8 text-lg text-muted-foreground max-w-2xl mx-auto leading-relaxed">
            Give us one hour. We'll learn your business like a marketing employee who's worked for you for a year — then build your strategy, write your posts, and get smarter every week.
          </p>
          <Button data-testid="footer-cta" size="lg" onClick={() => nav("/pricing")} className="mt-10 h-14 px-8 text-base">
            Pick your plan <ArrowRight className="h-5 w-5" />
          </Button>
        </div>
      </section>

      <footer className="border-t border-border py-8">
        <div className="max-w-7xl mx-auto px-6 flex flex-wrap items-center justify-between gap-4 text-sm text-muted-foreground">
          <div className="flex items-center gap-2">
            <Zap className="h-4 w-4 text-primary"/>
            <span className="font-semibold text-foreground">ContentOS</span>
            <span>· Your AI Organic Marketing Department</span>
          </div>
          <div className="flex flex-wrap items-center gap-4">
            <Link to="/terms" data-testid="footer-terms" className="hover:text-foreground">Terms</Link>
            <Link to="/privacy" data-testid="footer-privacy" className="hover:text-foreground">Privacy</Link>
            <Link to="/refunds" data-testid="footer-refunds" className="hover:text-foreground">Refunds</Link>
            <span className="font-mono text-xs">© 2026</span>
          </div>
        </div>
      </footer>
    </div>
  );
}
